import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import type { SavedStudyBuilderDraftSummary } from './studyBuilderDrafts'
import type { ShopifyAppBridge } from '../../shopifyApiFetch'
import type { VerifiedTestFeedConnectionScope } from '../../ShopifyAppContext'
import { useShopifyScopeLifecycle } from '../useShopifyScopeLifecycle'
import { projectStudyDeletionReceipts, useStudyDeletionReceipts } from './useStudyDeletionReceipts'
import { runStudyAuthoringDeletion } from '../../components/direct-studies/runStudyAuthoringDeletion'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { resolveMerchantErrorMessage, MerchantFacingError } from '../../merchantErrors'
import { trackShopifyAction } from '../../shopifyUiTracking'
import {
  canSelectStudyForDeletion,
  studyDeletionReceipt,
  studySetupRowId,
} from '../../components/study-deletion-eligibility'

export type BulkStudyDeletionItem = {
  id: string
  title: string
  productTitle: string
  action: 'delete' | 'check' | 'retry'
  status?: 'pending' | 'complete' | 'failed'
  operationId?: string
  error?: string
}

type BulkDeletionPhase = 'confirming' | 'running' | 'outcomes'
type BulkDeletionAction = 'delete' | 'check' | 'retry' | 'mixed'

function operationAction(draft: SavedStudyBuilderDraftSummary): BulkStudyDeletionItem['action'] {
  const receipt = studyDeletionReceipt(draft)
  if (receipt?.status === 'accepted' || receipt?.status === 'cleaning') return 'check'
  if (receipt?.status === 'failed') return 'retry'
  return 'delete'
}

function itemForDraft(draft: SavedStudyBuilderDraftSummary): BulkStudyDeletionItem {
  return {
    id: draft.studyId!,
    title: draft.title,
    productTitle: draft.productTitle ?? '',
    action: operationAction(draft),
    operationId: studyDeletionReceipt(draft)?.operationId,
  }
}

export function useBulkStudyDeletion({
  shopify,
  scope,
  drafts,
}: {
  shopify: ShopifyAppBridge
  scope: VerifiedTestFeedConnectionScope | null
  drafts: SavedStudyBuilderDraftSummary[]
}) {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState<BulkDeletionPhase>('confirming')
  const [items, setItems] = useState<BulkStudyDeletionItem[]>([])
  const [activeWorkItems, setActiveWorkItems] = useState<BulkStudyDeletionItem[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const confirmedItems = useRef<BulkStudyDeletionItem[]>([])
  const workItems = useRef<BulkStudyDeletionItem[]>([])
  const submitLock = useRef(false)
  const workStarted = useRef(false)
  const queryClient = useQueryClient()
  const active = useShopifyScopeLifecycle(
    scope ? `${scope.shopDomain}:${scope.orgId}:${scope.connectionId}:bulk-delete` : 'no-scope:bulk-delete',
  )

  const receiptQueries = useStudyDeletionReceipts(shopify, scope, drafts, items)
  const visibleItems = items.map((item): BulkStudyDeletionItem => {
    const query = item.operationId ? receiptQueries.get(item.operationId) : undefined
    const receipt = query?.data
    if (!receipt)
      return query?.error
        ? {
            ...item,
            error: resolveMerchantErrorMessage(
              query.error,
              shopifyComponentCopy.studySetupBulkDeleteDialog.statusUnavailable,
            ),
          }
        : item
    const status = receipt.status === 'completed' ? 'complete' : receipt.status === 'failed' ? 'failed' : 'pending'
    const error = query?.error
      ? resolveMerchantErrorMessage(query.error, shopifyComponentCopy.studySetupBulkDeleteDialog.statusUnavailable)
      : undefined
    return { ...item, status, action: status === 'failed' ? 'retry' : 'check', error }
  })

  const begin = () => {
    if (submitLock.current) {
      setOpen(true)
      return
    }
    const chosen = drafts.filter(
      (draft) => selectedIds.includes(studySetupRowId(draft.draftId)) && canSelectStudyForDeletion(draft),
    )
    const snapshot = chosen.map(itemForDraft)
    confirmedItems.current = snapshot
    workItems.current = snapshot
    setActiveWorkItems(snapshot)
    setItems(snapshot)
    setPhase('confirming')
    setErrorMessage(null)
    trackShopifyAction(shopify, 'confirm-delete', () => setOpen(true))
  }

  const mutation = useMutation({
    mutationFn: async () =>
      trackShopifyAction(shopify, 'delete-setup', async () => {
        const snapshot = [...workItems.current]
        workStarted.current = false
        setPhase('running')
        const token = active.token
        if (!active.isActive(token))
          throw new DOMException('STUDY_DELETION_VISIT_ENDED: Deletion visit ended', 'AbortError')
        // The confirmed server operation owns current eligibility and concurrent-write checks.
        const queue = [...snapshot]
        const failedIds = new Set<string>()
        let completedCount = 0
        workStarted.current = true
        const worker = async () => {
          for (;;) {
            if (!active.isActive(token))
              throw new DOMException('STUDY_DELETION_VISIT_ENDED: Deletion visit ended', 'AbortError')
            const item = queue.shift()
            if (!item) return
            updateItem(item.id, { status: 'pending', error: undefined })
            try {
              const outcome = await runStudyAuthoringDeletion({
                shopify,
                waitForCompletion: false,
                studyId: item.id,
                operationId: item.operationId,
                retryFailed: item.action === 'retry',
                isActive: () => active.isActive(token),
                onOperationId: (operationId) => {
                  updateItem(item.id, { operationId, action: 'check', status: 'pending' })
                  void queryClient.invalidateQueries({
                    queryKey: [
                      'shopify-study-authoring',
                      scope?.shopDomain,
                      scope?.orgId,
                      scope?.connectionId,
                      'deletion',
                      operationId,
                    ],
                    exact: true,
                  })
                },
                onReceipt: (receipt) => {
                  if (scope) {
                    const prefix = ['shopify-study-authoring', scope.shopDomain, scope.orgId, scope.connectionId]
                    queryClient.setQueryData([...prefix, 'deletion', receipt.operationId], receipt)
                    queryClient.setQueryData<SavedStudyBuilderDraftSummary[]>([...prefix, 'list'], (current) =>
                      current ? projectStudyDeletionReceipts(current, [receipt]) : current,
                    )
                  }
                  const status =
                    receipt.status === 'completed' ? 'complete' : receipt.status === 'failed' ? 'failed' : 'pending'
                  updateItem(item.id, {
                    operationId: receipt.operationId,
                    status,
                    action: status === 'failed' ? 'retry' : status === 'pending' ? 'check' : item.action,
                  })
                },
              })
              if (outcome.status === 'pending') continue
              completedCount++
              updateItem(item.id, { status: 'complete', error: undefined })
            } catch (error) {
              if (error instanceof DOMException && error.name === 'AbortError') throw error
              const knownOperation = currentItem(item.id)?.operationId
              const receiptFailure = typeof error === 'object' && error !== null && 'receipt' in error
              const retryable = Boolean(receiptFailure || (!knownOperation && item.action !== 'check'))
              const reason = resolveMerchantErrorMessage(error, shopifyComponentCopy.studySetupBulkDeleteDialog.failure)
              if (retryable) failedIds.add(item.id)
              updateItem(item.id, {
                status: retryable ? 'failed' : 'pending',
                action: retryable ? 'retry' : 'check',
                error: reason,
              })
            }
          }
        }
        await Promise.all([worker(), worker(), worker()])
        if (!active.isActive(token))
          throw new DOMException('STUDY_DELETION_VISIT_ENDED: Deletion visit ended', 'AbortError')
        setSelectedIds(
          confirmedItems.current.filter((item) => item.status === 'failed').map((item) => studySetupRowId(item.id)),
        )
        if (completedCount === 1) shopify.toast?.show(shopifyComponentCopy.studySetupBulkDeleteDialog.testDeletedToast)
        if (completedCount > 1)
          shopify.toast?.show(shopifyComponentCopy.studySetupBulkDeleteDialog.testsDeletedToast(completedCount))
        setPhase('outcomes')
        if (failedIds.size)
          throw new MerchantFacingError(shopifyComponentCopy.studySetupBulkDeleteDialog.partialFailure, {
            code: 'STUDY_DELETION_PARTIAL_FAILURE',
          })
      }),
    onError: (error) => {
      if (error instanceof DOMException && error.name === 'AbortError') return
      setPhase(
        !workStarted.current ||
          (error instanceof MerchantFacingError && error.code === 'STUDY_DELETION_ELIGIBILITY_CHANGED')
          ? 'confirming'
          : 'outcomes',
      )
      setErrorMessage(resolveMerchantErrorMessage(error, shopifyComponentCopy.studySetupBulkDeleteDialog.failure))
    },
    onSettled: () => {
      submitLock.current = false
      workStarted.current = false
    },
  })

  function currentItem(id: string) {
    return confirmedItems.current.find((item) => item.id === id)
  }

  function updateItem(id: string, changes: Partial<BulkStudyDeletionItem>) {
    confirmedItems.current = confirmedItems.current.map((item) => (item.id === id ? { ...item, ...changes } : item))
    workItems.current = workItems.current.map((item) => (item.id === id ? { ...item, ...changes } : item))
    setActiveWorkItems((current) => current.map((item) => (item.id === id ? { ...item, ...changes } : item)))
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...changes } : item)))
  }

  function runAction(action: 'confirm' | 'check' | 'retry') {
    if (submitLock.current) return
    let snapshot: BulkStudyDeletionItem[]
    if (action === 'confirm') snapshot = workItems.current
    else
      snapshot = visibleItems
        .filter(
          (item) =>
            (item.status === 'pending' && action === 'check') || (item.status === 'failed' && action === 'retry'),
        )
        .map((item) => ({ ...item, action: action === 'check' ? 'check' : 'retry', error: undefined }))
    if (snapshot.length === 0) return
    submitLock.current = true
    workItems.current = snapshot.map((item) => ({ ...item }))
    setActiveWorkItems(snapshot.map((item) => ({ ...item })))
    for (const item of snapshot) {
      if (action !== 'confirm') updateItem(item.id, { error: undefined })
    }
    setErrorMessage(null)
    if (action !== 'confirm') setPhase('running')
    mutation.mutate()
  }

  const actions = new Set(activeWorkItems.map((item) => item.action))
  const confirmationAction: BulkDeletionAction = actions.size === 1 ? [...actions][0]! : 'mixed'
  return {
    selectedIds,
    setSelectedIds,
    open,
    phase,
    confirmationAction,
    actionCount: activeWorkItems.length,
    canConfirm: activeWorkItems.length > 0,
    items: visibleItems,
    errorMessage,
    isDeleting: mutation.isPending,
    begin,
    close: () => setOpen(false),
    confirm: () => runAction('confirm'),
    checkPending: () => runAction('check'),
    retryFailed: () => runAction('retry'),
  }
}
